import type { FieldDef } from '@/modules/catalog'
import { numericIssue } from '@/shared/numeric'

export const freightBase = '/api/v1/sales/freights'
export const freightReadPermissions = ['sales.view_salesfreight', 'sales.view_salesoutbound',
  'masterdata.view_product_amount', 'masterdata.view_product_cost'] as const
export const freightRequestPermissions = [...freightReadPermissions, 'sales.request_salesfreight'] as const
export const freightMoneyField: FieldDef = { key: 'amount', label: '金额', labelEn: 'Amount', kind: 'decimal',
  numeric: { precision: 4, maxDigits: 18, min: '0' } }
export const freightTaxField: FieldDef = { key: 'tax', label: '税率%', labelEn: 'Tax %', kind: 'decimal',
  numeric: { precision: 4, maxDigits: 7, min: '0', max: '100' } }
export const freightRatioField: FieldDef = { key: 'ratio', label: '人工比例', labelEn: 'Manual ratio', kind: 'decimal',
  numeric: { precision: 6, maxDigits: 24, min: '0', max: '1' } }

export type FreightBasis = 'actual_kg' | 'untaxed_amount' | 'manual_ratio'
export interface FreightSource { id: number; doc_no: string; customer: string; warehouse: string }
export interface Carrier { id: number; code: string; name_zh: string; name_en: string }
export interface FreightFormLine extends FreightSource { charge_net: string; charge_tax_rate: string; weight: string }
export interface FreightForm { supplier_id: number | undefined; reference_no: string; invoice_no: string;
  invoice_date: string; estimated_net: string; actual_net: string; tax_rate: string;
  allocation_basis: FreightBasis; reason: string; lines: FreightFormLine[] }
export interface FreightLine { id: number; outbound_id: number; outbound_no: string; customer: string; warehouse?: string;
  charge_net: string | null; charge_tax_rate: string | null; charge_tax: string | null; charge_gross: string | null;
  weight: string | null; allocated_cost?: string | null; receivable_id: number | null }
export interface FreightHistory { approval_id: number; version: number; status: string;
  events: { action: string; actor: string; reason: string; at: string }[] }
export interface FreightDocument { id: number; doc_no: string; supplier_id: number; supplier_name: string;
  version: number; status: string; reference_no: string; invoice_no?: string; invoice_date?: string | null;
  estimated_net?: string | null; actual_net?: string | null; tax_rate?: string | null; tax_amount?: string | null;
  expense_gross?: string | null; payable_id?: number | null; payable_remaining?: string | null;
  allocation_basis: FreightBasis; reason: string; lines: FreightLine[]; approval_id: number | null;
  allowed_actions: string[]; history: FreightHistory[];
  command?: { id: number; version: number; replayed: boolean }; replayed?: boolean }
export interface FreightPreview { expense_tax: string; expense_gross: string;
  lines: { outbound_id: number; charge_tax: string; charge_gross: string; weight: string; allocated_cost: string }[] }
export interface FreightPending { path: string; method: 'POST' | 'PATCH'; body: string; key: string; target_id: number | null }
export const freightStorageKey = (actor: number) => `coldwise:freight:${actor}`
const positiveId = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value > 0
const exact = (value: string, field: FieldDef) => /^(0|[1-9]\d*)(\.\d+)?$/.test(value) && !numericIssue(value, field)
export function newFreightForm(): FreightForm {
  return { supplier_id: undefined, reference_no: '', invoice_no: '', invoice_date: '', estimated_net: '', actual_net: '',
    tax_rate: '', allocation_basis: 'actual_kg', reason: '', lines: [] }
}
export function freightPayload(form: FreightForm, version?: number) {
  if (!positiveId(form.supplier_id) || !form.reference_no.trim() || !form.reason.trim() || !form.lines.length
    || form.lines.length > 200 || new Set(form.lines.map(row => row.id)).size !== form.lines.length) throw new Error('input')
  const decimal = (value: string, field: FieldDef) => {
    if (value === '') return null
    if (!exact(value, field)) throw new Error('precision')
    return value
  }
  return { ...(version ? { expected_version: version } : {}), supplier_id: form.supplier_id,
    reference_no: form.reference_no.trim(), invoice_no: form.invoice_no.trim(), invoice_date: form.invoice_date || null,
    estimated_net: decimal(form.estimated_net, freightMoneyField), actual_net: decimal(form.actual_net, freightMoneyField),
    tax_rate: decimal(form.tax_rate, freightTaxField), allocation_basis: form.allocation_basis, reason: form.reason.trim(),
    lines: form.lines.map(row => {
      if (!positiveId(row.id)) throw new Error('source')
      return { outbound_id: row.id, charge_net: decimal(row.charge_net, freightMoneyField),
        charge_tax_rate: decimal(row.charge_tax_rate, freightTaxField),
        weight: form.allocation_basis === 'manual_ratio' ? decimal(row.weight, freightRatioField) : null }
    }) }
}
export function parseFreightPending(raw: string): FreightPending {
  const value = JSON.parse(raw) as FreightPending
  if (!value || typeof value.path !== 'string' || !['POST', 'PATCH'].includes(value.method)
    || typeof value.key !== 'string' || !/^[\da-f]{8}(-[\da-f]{4}){3}-[\da-f]{12}$/i.test(value.key)
    || typeof value.body !== 'string' || value.body.length > 100000) throw new Error('pending')
  const body = JSON.parse(value.body) as Record<string, unknown>
  if (!body || typeof body !== 'object' || Array.isArray(body)) throw new Error('body')
  const save = value.path === freightBase || /^\/api\/v1\/sales\/freights\/[1-9]\d*$/.test(value.path)
  const submit = /^\/api\/v1\/sales\/freights\/[1-9]\d*\/submit$/.test(value.path)
  const withdraw = /^\/api\/v1\/workflow\/instances\/[1-9]\d*\/withdraw$/.test(value.path)
  if (save) {
    if (value.method !== (value.path === freightBase ? 'POST' : 'PATCH') || !positiveId(body.supplier_id)
      || typeof body.reference_no !== 'string' || !body.reference_no.trim() || typeof body.reason !== 'string'
      || !body.reason.trim() || !Array.isArray(body.lines) || !body.lines.length || body.lines.length > 200
      || !['actual_kg', 'untaxed_amount', 'manual_ratio'].includes(String(body.allocation_basis))) throw new Error('save')
    const rows = body.lines as Record<string, unknown>[]
    if (rows.some(row => !row || !positiveId(row.outbound_id))
      || new Set(rows.map(row => row.outbound_id)).size !== rows.length) throw new Error('source')
    for (const row of [body, ...rows]) for (const [key, field] of Object.entries({ estimated_net: freightMoneyField,
      actual_net: freightMoneyField, tax_rate: freightTaxField, charge_net: freightMoneyField,
      charge_tax_rate: freightTaxField, weight: freightRatioField })) {
      const number = row[key]
      if (number !== undefined && number !== null && (typeof number !== 'string' || !exact(number, field))) throw new Error('decimal')
    }
  } else if ((submit || withdraw) && value.method === 'POST') {
    const reason = body[withdraw ? 'comment' : 'reason']
    if (typeof reason !== 'string' || !reason.trim()) throw new Error('reason')
  } else throw new Error('path')
  if (value.path !== freightBase && !positiveId(body.expected_version)) throw new Error('version')
  if (value.path === freightBase ? value.target_id !== null : !positiveId(value.target_id)) throw new Error('target')
  if ((save && value.path !== freightBase || submit) && Number(value.path.split('/')[5]) !== value.target_id) throw new Error('target')
  return value
}
